import { css, mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { TripType } from '@eti/schema-types';
import type { SortType } from '@eti/schema-types';
import { price as priceUtil } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { Inline, vars } from '@etg/wings';
import { useState } from 'react';
import { useResultPageContext } from '../contexts/ResultPageContext';
import MobileSort from './MobileSort';
import QuickSortButton from './QuickSortButton';
import {
  getLongestQuickSortOption,
  getQuickSortOptions,
  getRecommendedQuickSortOption,
} from './utils';
import { quickSortPricesType } from './quickSortConstants';
import QuickSortDropdown from './QuickSortDropdown';

const firstQuicksortButtonStyles = css`
  border-radius: ${vars.card.borderRadius} 0 0 ${vars.card.borderRadius};

  [dir='rtl'] & {
    border-radius: 0 ${vars.card.borderRadius} ${vars.card.borderRadius} 0;
    border-right: 0;
  }
`;

const quickSortButtonsWrapperStyles = css`
  justify-content: space-between;
`;

const borderRadiusStyles = css`
  border-radius: 0 ${vars.card.borderRadius} ${vars.card.borderRadius} 0;
  border-right: 0;

  [dir='rtl'] & {
    border-radius: ${vars.card.borderRadius} 0 0 ${vars.card.borderRadius};
    border-right: 1px solid ${vars.colors.divider};
  }
`;

interface QuickSortButtonBarProps {
  isOpen: boolean;
  onDismiss?: (value: boolean) => void;
  selectedSortType: SortType;
  sortPrices: {
    bestValue: number;
    carrierPromo: number;
    cheapTrip: number;
    recommendation: number;
    shortTrip: number;
  };
}

const QuickSortButtonBar = ({
  isOpen,
  onDismiss,
  selectedSortType,
  sortPrices,
}: QuickSortButtonBarProps) => {
  const { t } = useTranslation();
  const { searchData, handleSort } = useResultPageContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean | null>(null);
  const quickSortOptions = getQuickSortOptions(t);
  const recommendedQuickSortOption = getRecommendedQuickSortOption(t);
  const journeyType = searchData?.type;

  const mobileSortingOptions = quickSortOptions.concat(
    recommendedQuickSortOption,
    getLongestQuickSortOption(t),
  );

  const onButtonClick = (value: keyof typeof quickSortPricesType) => {
    setIsDropdownOpen(false);
    handleSort(value);
  };

  return (
    <Inline
      aria-label={t('ScreenReader.Result.Quicksort.Section.Label')}
      className={quickSortButtonsWrapperStyles}
      data-testid="Quicksort-button-bar-container-v2"
      noWrap
      role="radiogroup"
      spacing={0}
    >
      <Media query={mediaQueries.medium.down}>
        {(isMobile) =>
          isMobile ? (
            <MobileSort
              isOpen={isOpen}
              onDismiss={onDismiss}
              onDone={handleSort}
              options={mobileSortingOptions}
              sortPrices={sortPrices}
            />
          ) : (
            <>
              {quickSortOptions.map(({ value, labelKey, desc }, index: number) => (
                <QuickSortButton
                  className={index === 0 ? firstQuicksortButtonStyles : undefined}
                  desc={desc}
                  formattedPrice={priceUtil.formatPrice(
                    sortPrices[quickSortPricesType[value] as keyof typeof sortPrices],
                  )}
                  id={index === 0 ? 'first-quick-sort-button-v2' : undefined}
                  isSelected={selectedSortType === value}
                  key={labelKey + index}
                  label={labelKey}
                  onClick={() =>
                    journeyType === TripType.Return ? onButtonClick(value) : handleSort(value)
                  }
                  type={value}
                />
              ))}
              {journeyType !== TripType.Return ? (
                <QuickSortButton
                  className={borderRadiusStyles}
                  desc={recommendedQuickSortOption.desc}
                  formattedPrice={priceUtil.formatPrice(
                    sortPrices[
                      quickSortPricesType[
                        recommendedQuickSortOption.value
                      ] as keyof typeof sortPrices
                    ],
                  )}
                  isSelected={selectedSortType === recommendedQuickSortOption.value}
                  label={recommendedQuickSortOption.labelKey}
                  onClick={() => handleSort(recommendedQuickSortOption.value)}
                  type={recommendedQuickSortOption.value}
                />
              ) : (
                <QuickSortDropdown
                  isDropdownOpen={isDropdownOpen}
                  onButtonClick={handleSort}
                  selectedSortType={selectedSortType}
                  setIsDropdownOpen={setIsDropdownOpen}
                  sortPrices={sortPrices}
                />
              )}
            </>
          )
        }
      </Media>
    </Inline>
  );
};

export default QuickSortButtonBar;
